import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CSRF_COOKIE = "redkey_admin_csrf";
const CSRF_HEADER = "X-CSRF-Token";
const LEGACY_TOKEN_KEY = "redkey_admin_token";
const SAFE_METHODS = new Set(["get", "head", "options"]);

// Read a cookie value from document.cookie (client-readable cookies only —
// the httpOnly auth cookie is invisible to JS by design).
function readCookie(name) {
  const prefix = `${encodeURIComponent(name)}=`;
  const item = document.cookie
    .split("; ")
    .find((row) => row.startsWith(prefix));
  return item ? decodeURIComponent(item.slice(prefix.length)) : null;
}

// Authed instance — the browser attaches the httpOnly auth cookie
// automatically because of `withCredentials: true`. The request
// interceptor echoes the raw part of the readable CSRF cookie in
// `X-CSRF-Token` on every state-changing method.
export const adminApi = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});
adminApi.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (!SAFE_METHODS.has(method)) {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) {
      const raw = csrf.split(".", 1)[0];
      config.headers = config.headers || {};
      config.headers[CSRF_HEADER] = raw;
    }
  }
  return config;
});

// Bare axios instance for /admin/login — same withCredentials so the
// Set-Cookie response is accepted, but no auth cookie is expected yet.
const authApi = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

export function AuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  // Bootstrap: ask the backend who we are. The httpOnly cookie (if any)
  // is attached automatically by the browser.
  useEffect(() => {
    // One-time cleanup of the pre-migration localStorage token.
    try { localStorage.removeItem(LEGACY_TOKEN_KEY); } catch (_) { /* ignore */ }
    let mounted = true;
    adminApi
      .get("/admin/me")
      .then((r) => mounted && setAdmin(r.data))
      .catch(() => { /* not logged in — fine */ })
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await authApi.post("/admin/login", { email, password });
    setAdmin(data.admin);
    return data.admin;
  }, []);

  const logout = useCallback(async () => {
    // Update UI state immediately for snappy UX.
    setAdmin(null);
    try {
      await adminApi.post("/admin/logout");
    } catch (_) {
      // Server-side cookie clear failed; the browser will keep the
      // cookie until it expires, but the client-side session is gone.
    }
  }, []);

  return (
    <AuthCtx.Provider
      value={useMemo(
        () => ({ admin, loading, login, logout }),
        [admin, loading, login, logout]
      )}
    >
      {children}
    </AuthCtx.Provider>
  );
}
